import {isFeatureEnabled} from 'feature-flags';
import {
  Attributes,
  AttributesRow,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  Flags,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Grid, Heading, HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {assoc, isNil, isNotNil} from 'ramda';
import {isArray, noop} from 'ramda-adjunct';

import {useGetBranchQuery, useReadCodelistQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {
  RentalVehicleBlockListItemResponseBody,
  useCreateRentalVehicleBlockMutation,
  useDeleteRentalVehicleBlockMutation,
  useEditRentalVehicleBlockMutation,
  useGetRentalVehicleQuery,
  useListRentalVehicleBlockQuery,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  CommentsWidget,
  DmsDataGrid,
  type DmsDataGridProps,
  handleApiError,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {ApiError, composePath, parseDate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {RentalVehicleBlockConflictDialog} from './components/RentalVehicleBlockConflictDialog';
import {RentalVehicleBlockForm} from './components/RentalVehicleBlockForm';
import {getBlockConflictErrorMessage} from './utils/getBlockConflictErrorMessage';

// Fixed contract with the backend grid definition - do not rename.
const RENTAL_VEHICLE_BLOCK_GRID_CODE = 'rental-vehicle-block';

interface VehicleRentalProps extends Pick<DmsDataGridProps, 'maximization'> {
  vehicleId: string;
}

export function VehicleRental(props: VehicleRentalProps) {
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId: props.vehicleId});
  useGetBranchQuery(
    {branchId: rentalVehicle?.branchId ?? ''},
    {
      skip: !rentalVehicle?.branchId,
    }
  );

  const formatDateTimeShort = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const {
    data: carParkData,
    isLoading: isLoadingCarPark,
    isError: isCarParkError,
  } = useReadCodelistQuery({codelistId: 'carPark'});

  const {
    data: vehicleCategoryData,
    isLoading: isLoadingVehicleCategory,
    isError: isVehicleCategoryError,
  } = useReadCodelistQuery({codelistId: 'vehicleCategory'});

  const {
    data: equipmentData,
    isLoading: isLoadingEquipment,
    isError: isEquipmentError,
  } = useReadCodelistQuery({codelistId: 'equipment'});

  const isLoading = isLoadingCarPark || isLoadingVehicleCategory || isLoadingEquipment;
  const isError = isCarParkError || isVehicleCategoryError || isEquipmentError;

  const [hasReadRentalVehicleBlockPermission, hasEditRentalVehicleBlockPermission] = usePermissions(
    {
      permissionKeys: ['readRentalVehicle', 'editRentalVehicle'],
    }
  );

  // Behind a flag until the block endpoints and grid ship on the backend: while it is off,
  // the section neither renders nor makes any block request.
  const isVehicleBlocksSectionVisible =
    isFeatureEnabled(featureFlags.RENTAL_VEHICLE_BLOCKS) && hasReadRentalVehicleBlockPermission;

  // Kept solely so actionCallback can resolve the full block object that handleEditBlock /
  // handleDeleteBlock expect - the grid itself renders the rows now.
  const {data: blocksData} = useListRentalVehicleBlockQuery(
    {vehicleId: props.vehicleId},
    {skip: !isVehicleBlocksSectionVisible}
  );

  const {data: blockReasonData} = useReadCodelistQuery(
    {codelistId: 'vehicleBlock'},
    {skip: !isVehicleBlocksSectionVisible}
  );

  const [createRentalVehicleBlock] = useCreateRentalVehicleBlockMutation();
  const [editRentalVehicleBlock] = useEditRentalVehicleBlockMutation();
  const [deleteRentalVehicleBlock] = useDeleteRentalVehicleBlockMutation();

  const blocks = blocksData?.blocks ?? [];

  const blockReasonOptions =
    blockReasonData?.codes?.map((code) => ({value: code.codeId, label: code.name})) ?? [];

  const addBlockLabel = i18n.t('entity.rental.actions.addVehicleBlock');
  const editBlockLabel = i18n.t('entity.rental.actions.editVehicleBlock');

  const handleBlockSubmitError = (error: ApiError) => {
    const conflictMessage = getBlockConflictErrorMessage(error);

    if (isNotNil(conflictMessage)) {
      openDialog(<RentalVehicleBlockConflictDialog message={conflictMessage} />, {
        size: 'small',
        'data-testid': 'rentalVehicleBlockConflict',
      });
      return;
    }

    handleApiError(error);
  };

  const refreshBlocksGrid = () => refreshDmsDatagrid(RENTAL_VEHICLE_BLOCK_GRID_CODE);

  const handleAddBlock = () =>
    openDialog(
      <RentalVehicleBlockForm
        reasonOptions={blockReasonOptions}
        submitButtonTitle={i18n.t('general.actions.create')}
        onSubmit={async (values) => {
          await createRentalVehicleBlock({
            vehicleId: props.vehicleId,
            createRentalVehicleBlockRequestBody: {
              from: values.from.toISOString(),
              to: values.to.toISOString(),
              reasonId: values.reasonId,
              note: values.note,
            },
          })
            .unwrap()
            .then(() => {
              closeCurrentDialog();
              showNotification.success(i18n.t('general.notifications.successfullyCreated'));
              refreshBlocksGrid();
            })
            .catch(handleBlockSubmitError);
        }}
      />,
      {
        title: addBlockLabel,
      }
    );

  const handleEditBlock = (block: RentalVehicleBlockListItemResponseBody) =>
    openDialog(
      <RentalVehicleBlockForm
        reasonOptions={blockReasonOptions}
        defaultValues={{
          from: parseDate(block.from),
          to: parseDate(block.to),
          reasonId: block.reasonId,
          note: block.note,
        }}
        submitButtonTitle={i18n.t('general.actions.edit')}
        onSubmit={async (values) => {
          await editRentalVehicleBlock({
            vehicleId: props.vehicleId,
            blockId: block.blockId,
            editRentalVehicleBlockRequestBody: {
              from: values.from.toISOString(),
              to: values.to.toISOString(),
              reasonId: values.reasonId,
              note: values.note,
            },
          })
            .unwrap()
            .then(() => {
              closeCurrentDialog();
              showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
              refreshBlocksGrid();
            })
            .catch(handleBlockSubmitError);
        }}
      />,
      {
        title: editBlockLabel,
      }
    );

  const handleDeleteBlock = (block: RentalVehicleBlockListItemResponseBody) =>
    openDeleteDialog({
      onConfirm: async () =>
        await deleteRentalVehicleBlock({
          vehicleId: props.vehicleId,
          blockId: block.blockId,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
            refreshBlocksGrid();
          })
          .catch(handleApiError),
    });

  const blocksQueryModifier = assoc('vehicleId', props.vehicleId);

  const blocksActionCallback: ActionCallback = ({actionKey, rowId}) => {
    const blockId = isArray(rowId) ? rowId[0] : rowId;
    const block = blocks.find((item) => item.blockId === blockId);

    if (isNil(block)) {
      return;
    }

    match(actionKey)
      .with('edit', () => handleEditBlock(block))
      .with('delete', () => handleDeleteBlock(block))
      .otherwise(noop);
  };

  const carParkName = carParkData?.codes?.find(
    (code) => code.codeId === rentalVehicle?.carParkId
  )?.name;
  const vehicleCategoryName = vehicleCategoryData?.codes?.find(
    (code) => code.codeId === rentalVehicle?.vehicleCategoryId
  )?.name;
  const equipmentNames = equipmentData?.codes
    ?.filter((code) => rentalVehicle?.equipment?.includes(code.codeId))
    .map((code) => ({
      label: code.name,
    }));

  const rows: AttributesRow[] = rentalVehicle
    ? [
        {
          label: i18n.t('entity.rental.labels.carParkId'),
          value: carParkName,
        },
        {
          label: i18n.t('entity.rental.labels.vehicleCategory'),
          value: vehicleCategoryName,
        },
        {
          label: i18n.t('general.labels.active'),
          value: rentalVehicle.active ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
        },
        {
          label: i18n.t('general.labels.status'),
          value: i18n.t(`entity.rental.labels.${rentalVehicle.state}`),
        },
        {
          label: i18n.t('entity.rental.labels.availableFrom'),
          value: rentalVehicle.availability.from
            ? formatDateTimeShort('dateTimeShort', parseDate(rentalVehicle.availability.from))
            : null,
        },
        {
          label: i18n.t('entity.rental.labels.availableTo'),
          value: rentalVehicle.availability.to
            ? formatDateTimeShort('dateTimeShort', parseDate(rentalVehicle.availability.to))
            : null,
        },
        {
          label: i18n.t('entity.rental.labels.mileageLimit'),
          value:
            rentalVehicle.availability.mileageLimit !== null
              ? `${formatNumber(rentalVehicle.availability.mileageLimit)} ${i18n.t('general.metric.km')}`
              : null,
        },
        {
          label: i18n.t('entity.rental.labels.note'),
          value: rentalVehicle.note,
        },
        {
          label: i18n.t('entity.rental.labels.equipment'),
          content: (
            <Flags flags={equipmentNames} size="small" isSubtle colorScheme="blue" shouldWrap />
          ),
        },
      ]
    : [];

  return (
    <VStack spacing={6}>
      <Grid columns={2}>
        <Card
          title={i18n.t('entity.vehicle.labels.rentalInformation')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              to: composePath(vehiclesRoutes.editRentalVehicleV2, {
                params: {id: props.vehicleId},
              }),
              leftIcon: 'image/edit',
              title: i18n.t('general.actions.edit'),
              size: 'small',
            },
          ]}
        >
          <DataStatus isLoading={isLoading} isError={isError}>
            <Attributes rows={rows} />
          </DataStatus>
        </Card>

        <VStack>
          <CommentsWidget
            resourceId={EntityResourceIds.vehicle}
            recordId={props.vehicleId}
            title={i18n.t('entity.vehicle.labels.vehicleComments')}
          />
        </VStack>
      </Grid>

      <Show when={isVehicleBlocksSectionVisible}>
        <VStack spacing={4}>
          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('entity.rental.labels.vehicleBlock')}</Heading>
            <Show when={hasEditRentalVehicleBlockPermission}>
              <Button
                variant="link"
                title={addBlockLabel}
                leftIcon="content/add_circle"
                onClick={handleAddBlock}
                data-testid="addRentalVehicleBlock"
              />
            </Show>
          </HStack>

          <DmsDataGrid
            maximization={props.maximization}
            autoHeight
            gridCode={RENTAL_VEHICLE_BLOCK_GRID_CODE}
            // Without the edit permission the grid gets no callback at all, so no row action
            // can open a mutation dialog, whatever the backend grid definition renders.
            actionCallback={hasEditRentalVehicleBlockPermission ? blocksActionCallback : undefined}
            queryModifier={blocksQueryModifier}
            data-testid="rentalVehicleBlocksGrid"
          />
        </VStack>
      </Show>
    </VStack>
  );
}
